/**
 * One machine, in one place (`/workbenches/:deviceId?view=`): a workbench or a node, laid out as AI Workbench lays
 * itself out. The header names the machine, whose it is and how it is reached (one readout). The navigation (a
 * SubnavLayout, as Settings and a project's page: a column beside the view, a picker on a narrow window) starts with the machine's own views (Overview, Sessions, Terminal or a
 * node's Console, Apps, Activity, Settings), then the app's groups (Work, Project, Automation, Knowledge), and ends
 * with the screen as one quiet item: the native views carry the work, so viewing the screen is the fallback.
 *
 * Every action is the machine's own Control API over the relay (POST /relay/v1/devices/:id/rpc) or a relay channel;
 * the machine checks scopes and policy and audits the call under the real actor. Each view asks only for what it
 * shows, only while it is open, at most every 30 seconds and never while the tab is hidden; live views follow the
 * machine's own streams. The screen starts only when the person opens it.
 */

import { ArrowLeft, Display, Ellipsis } from '@gravity-ui/icons';
import { Dropdown, Label } from '@heroui/react';
import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import { Link, useSearchParams, useParams } from 'react-router-dom';

import { remotePath, routes } from '../api/client.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { useCloudStanding } from '../cloudPlan.ts';
import { ClearOrganisationDataDialog, ClearOrganisationDataStatus } from '../components/ClearOrganisationData.tsx';
import { useMachineScopes } from '../components/machineAllows.tsx';
import { connectionOf, useOwnerWords } from '../components/machineWords.tsx';
import { Button, ConfirmDialog, ErrorNote, Page, StatusMark, ViewHeader } from '../components/ui.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { SubnavLayout, type SubnavGroup } from '../components/ui/Layout.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { isCloud } from '../edition.ts';
import { useMay } from '../permissions.ts';
import { MachineCtx, type MachineContext } from '../remote/machineContext.ts';
import { useWorkbench } from '../remote/useWorkbench.ts';
import { ViewMetaContext } from '../remote/viewMeta.ts';
import { resolveView, viewsFor, VIEWS, type ViewId } from '../remote/views/machineViews.tsx';

const tone = VIEW_TONES.workbenches!;
const PLATFORM: Record<string, string> = { darwin: 'macOS', windows: 'Windows', linux: 'Linux' };

export function WorkbenchPage() {
  const { deviceId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { cloud } = useAuth();
  const standing = useCloudStanding();
  // Every plan has the relay: remote control of a person's own machines is part of the free account.
  const relay = true;
  // An administrator's views of someone's machine: Manage on workbenches (today: administrators).
  const managesMachines = useMay('machines.workbenches', 'manage');
  const admin = isCloud() ? (cloud?.organisation.role === 'owner' || cloud?.organisation.role === 'admin') : managesMachines === true;

  // The device record decides the kind; until it answers, the asked-for view is kept so a deep link does not jump.
  const [kindSeen, setKindSeen] = useState<'workbench' | 'node'>();
  const kind = kindSeen ?? 'workbench';
  const offer = { kind, relay, screen: standing.teams };
  const view = resolveView(params.get('view'), offer);
  const meta = VIEWS[view];
  const w = useWorkbench(deviceId, meta.needs ?? [], { relay });
  const d = w.device.data;
  const dKind = d ? (d.kind === 'node' ? 'node' : 'workbench') : undefined;
  if (dKind && dKind !== kindSeen) setKindSeen(dKind);
  const scopes = useMachineScopes(deviceId);
  const name = d ? (d.kind === 'node' ? (d.capabilities?.name ?? d.hostname) : d.hostname) : 'This machine';
  const owner = useOwnerWords(d);
  const via = useSyncExternalStore(remotePath.subscribe, remotePath.get);
  const conn = connectionOf(d, via);

  const go = useCallback(
    (v: string, extra: Record<string, string> = {}) => {
      const next = new URLSearchParams();
      if (v !== 'overview') next.set('view', v);
      for (const [k, x] of Object.entries(extra)) next.set(k, x);
      setParams(next);
    },
    [setParams],
  );
  // In sentences ("Asking jp-laptop for its sessions"); before the record answers, "the machine".
  const spoken = d ? name : 'the machine';
  const subject = d ? name : 'The machine';
  const machine = useMemo<MachineContext>(() => ({ scopes, kind, name: spoken, subject, go }), [scopes, kind, spoken, subject, go]);
  const groups = viewsFor(offer);
  // The views as links: each is an address of its own (`?view=`), so Back, a bookmark and a new tab all work.
  const base = `/workbenches/${encodeURIComponent(deviceId)}`;
  const href = (v: ViewId) => (v === 'overview' ? base : `${base}?view=${encodeURIComponent(v)}`);
  const nav: SubnavGroup[] = [
    ...groups.groups.map((g) => ({ label: g.label, items: g.views.map((v) => ({ key: v, label: VIEWS[v].title, to: href(v) })) })),
    ...(groups.quiet.length ? [{ label: '', items: groups.quiet.map((v) => ({ key: v, label: VIEWS[v].navLabel ?? VIEWS[v].title, to: href(v) })) }] : []),
  ];

  const [revoking, setRevoking] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearVersion, setClearVersion] = useState(0);
  const [problem, setProblem] = useState<string>();

  const blurb = d ? [owner, d.kind === 'node' ? `A node on ${d.hostname}` : undefined, PLATFORM[d.platform] ?? d.platform].filter(Boolean).join(' · ') : undefined;
  const offlineLine = relay && meta.relay && d && !d.relayConnected ? `${name} is ${d.online ? 'online but not taking remote control right now' : `offline, ${conn.detail}`}. This shows what it last reported and carries on when it is back.` : undefined;

  return (
    <SubnavLayout label="This machine" groups={nav} current={view}>
      <Page>
        <ViewHeader
          tone={tone}
          icon={<Display />}
          title={name}
          blurb={blurb}
          actions={
            <>
              <Link to="/workbenches" className="inline-flex items-center gap-1 text-xs text-muted hover:text-foreground">
                <ArrowLeft className="size-3.5" aria-hidden="true" />
                All workbenches
              </Link>
              {d ? (
                <span className="flex min-w-0 items-center gap-1.5 rounded-lg bg-surface-secondary px-2.5 py-1 text-xs" title={d.relayConnected ? `Connected ${conn.detail}` : conn.detail}>
                  <StatusMark state={conn.state} label={conn.label} />
                  <span className="hidden text-muted sm:inline">· {conn.detail}</span>
                </span>
              ) : null}
              <Dropdown>
                <Button size="sm" variant="ghost" isIconOnly aria-label={`More for ${name}`}>
                  <Ellipsis className="size-4" />
                </Button>
                <Dropdown.Popover placement="bottom end" className="min-w-52">
                  <Dropdown.Menu aria-label={`More for ${name}`} onAction={(k) => (k === 'revoke' ? setRevoking(true) : k === 'clear' ? setClearing(true) : undefined)}>
                    <Dropdown.Item id="revoke" textValue="Revoke access" variant="danger">
                      <Label>Revoke access…</Label>
                    </Dropdown.Item>
                    {admin ? (
                      <Dropdown.Item id="clear" textValue="Clear the organisation's data" variant="danger">
                        <Label>Clear the organisation’s data…</Label>
                      </Dropdown.Item>
                    ) : null}
                  </Dropdown.Menu>
                </Dropdown.Popover>
              </Dropdown>
            </>
          }
        />
        <ErrorNote error={w.device.error ?? problem} />
        <ClearOrganisationDataStatus deviceId={deviceId} version={clearVersion} />
        <MachineCtx.Provider value={machine}>
          <div className="min-w-0">
            {offlineLine ? <p className="m-0 mb-3 text-sm text-muted">{offlineLine}</p> : null}
            <ErrorNote error={w.error} />
            <div className={meta.fill ? 'h-[max(30rem,calc(100dvh-15rem))] min-h-0' : ''}>
              <PartBoundary key={view} name={meta.title} tone={meta.tone}>
                <ViewMetaContext.Provider value={meta}>{meta.render({ w, relay, admin, screen: standing.teams })}</ViewMetaContext.Provider>
              </PartBoundary>
            </div>
          </div>
        </MachineCtx.Provider>
        {clearing ? <ClearOrganisationDataDialog deviceId={deviceId} name={name} kind={kind} onClose={() => setClearing(false)} onDone={() => { setClearVersion((v) => v + 1); w.device.reload(); }} /> : null}
        <ConfirmDialog
          isOpen={revoking}
          title={`Revoke access for ${name}?`}
          confirmLabel="Revoke access"
          danger
          onCancel={() => setRevoking(false)}
          onConfirm={async () => {
            setProblem(undefined);
            try {
              await routes.revoke(deviceId);
              w.device.reload();
            } catch (e) {
              setProblem(`Revoke: ${(e as Error).message}`);
            }
            setRevoking(false);
          }}
        >
          <p>It stops reporting, receiving policies and answering remote control at once, and must enrol again with a new code. Its history and audit records stay.</p>
        </ConfirmDialog>
      </Page>
    </SubnavLayout>
  );
}
